import React, { useEffect, useState } from "react";
import { ExternalLink, LogOut, FileText, Image as ImageIcon, KeyRound, CheckCircle2, AlertCircle, Briefcase, Home, Users } from "lucide-react";
import TiltCard from "../components/TiltCard";
import PageBanner from "../components/PageBanner";
import SiteDriveAdmin from "../components/SiteDriveAdmin";
import CareersAdmin from "../components/CareersAdmin";
import UsersAdmin from "./UsersAdmin";
import { pageBanners } from "../data/mock";
import { apiUrl, hasApi, NO_BACKEND_MESSAGE, authHeaders, SESSION_EXPIRED_MESSAGE, clearClientSession, isAuthFailure } from "../lib/api";
import { canEditTab, canViewTab, isAdmin, visiblePortalTabs } from "../lib/access";

const TAB_ICONS = { home: Home, circulars: FileText, gallery: ImageIcon, careers: Briefcase, password: KeyRound, users: Users };
const HASH_TABS = ["home", "circulars", "gallery", "careers", "password", "users"];

function tabFromLocation() {
  const hash = (typeof window !== "undefined" ? window.location.hash : "").replace("#", "").toLowerCase();
  return HASH_TABS.includes(hash) ? hash : "home";
}

export default function Dashboard({ user, portalLink, onLogout }) {
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [tab, setTab] = useState(tabFromLocation);
  const [msg, setMsg] = useState(null);
  const [ceremonyState, setCeremonyState] = useState("loading");
  const [ceremonyBusy, setCeremonyBusy] = useState(false);
  const [ceremonyMsg, setCeremonyMsg] = useState(null);
  const admin = isAdmin(user);
  const tabs = visiblePortalTabs(user);

  const canSee = (id) => tabs.some((t) => t.id === id);
  const active = canSee(tab) ? tab : "home";

  const go = (id) => {
    if (!canSee(id)) return;
    setTab(id);
    if (typeof window !== "undefined") window.location.hash = id === "home" ? "home" : id;
  };

  useEffect(() => {
    const onHash = () => setTab(tabFromLocation());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    if (!admin || !hasApi()) {
      if (admin && !hasApi()) setCeremonyState("unavailable");
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(apiUrl("/api/ceremony"), { cache: "no-store", headers: { Accept: "application/json" } });
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok) {
          setCeremonyState("unavailable");
          return;
        }
        setCeremonyState(d.enabled === true ? "on" : "off");
      } catch {
        if (!cancelled) setCeremonyState("unavailable");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [admin]);

  const changePwd = async (e) => {
    e.preventDefault(); setMsg(null);
    if (!hasApi()) { setMsg({ ok: false, text: NO_BACKEND_MESSAGE }); return; }
    const r = await fetch(apiUrl("/api/auth/change-password"), { method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(pwd) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok && isAuthFailure(r.status, d.detail)) {
      clearClientSession();
      setMsg({ ok: false, text: SESSION_EXPIRED_MESSAGE });
      return;
    }
    setMsg({ ok: r.ok, text: r.ok ? "Password updated." : (d.detail || "Could not update password") });
    if (r.ok) setPwd({ current_password: "", new_password: "" });
  };

  const setCeremony = async (enabled) => {
    setCeremonyMsg(null);
    if (!hasApi()) {
      setCeremonyState("unavailable");
      setCeremonyMsg({ ok: false, text: "The APMERCL server is not connected, so the inauguration switch cannot be changed." });
      return;
    }
    setCeremonyBusy(true);
    try {
      const r = await fetch(apiUrl("/api/admin/ceremony"), {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ enabled }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok && isAuthFailure(r.status, d.detail)) {
        clearClientSession();
        setCeremonyMsg({ ok: false, text: SESSION_EXPIRED_MESSAGE });
        return;
      }
      if (!r.ok) {
        const detail = typeof d.detail === "string" ? d.detail : "";
        const text = r.status === 404
          ? "The inauguration switch is not on the server yet. Redeploy the API, then try again."
          : (detail || "The inauguration switch did not change. Try again.");
        setCeremonyMsg({ ok: false, text });
        return;
      }
      const on = d.enabled === true;
      setCeremonyState(on ? "on" : "off");
      setCeremonyMsg({
        ok: true,
        text: on
          ? "Inauguration is on. A new browser session sees the public inauguration screen."
          : "Inauguration is off. Visitors go straight to the site.",
      });
    } catch {
      setCeremonyMsg({ ok: false, text: "Could not reach the APMERCL server. Check your connection and try again." });
    } finally {
      setCeremonyBusy(false);
    }
  };

  const accessLine = tabs.filter((t) => t.id !== "home" && t.id !== "password").map((t) => {
    if (t.id === "users") return "Users (admin)";
    const level = canEditTab(user, t.id) ? "edit" : "view";
    return `${t.label} ${level}`;
  }).join(" · ") || "Home only";

  return (
    <div className="page-enter" data-testid="dashboard">
      <PageBanner image={pageBanners.login} title={`Welcome, ${user.name}`} crumb="Portal" subtitle={`${user.designation} · ${accessLine}`} />
      <section style={{ padding: "28px 24px 70px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gap: 18 }}>

          <TiltCard intensity={3}>
            <div style={{ padding: 22, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 16, background: "linear-gradient(135deg,#FFF7ED,#FEFCE8)", borderRadius: 20 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: "#EA580C" }}>INTERNAL PORTAL</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#1C1917", marginTop: 4 }}>APMERCL workspace</div>
                <div style={{ fontSize: 13.5, color: "#57534E", marginTop: 4 }}>Signed in as <b>{user.username}</b> · {user.designation}</div>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <a href={portalLink || "#"} target="_blank" rel="noreferrer" className="btn-primary-3d" data-testid="portal-details-link">Click here to know more details <ExternalLink size={14} /></a>
                <button className="btn-outline-3d" onClick={onLogout} data-testid="logout-btn"><LogOut size={14} /> Sign out</button>
              </div>
            </div>
          </TiltCard>

          {user.must_change && (
            <div data-testid="must-change-banner" style={{ padding: "12px 16px", background: "#FEF3C7", border: "1px solid rgba(202,138,4,0.35)", borderRadius: 12, color: "#854D0E", fontSize: 13.5, fontWeight: 600 }}>
              This is a temporary password — please change it in the Change password tab. You can still upload photographs and files while signed in.
              <button type="button" className="btn-outline-3d" onClick={() => go("password")} data-testid="must-change-goto" style={{ marginLeft: 10, padding: "6px 12px", fontSize: 12 }}>Open Change password</button>
            </div>
          )}

          <div className="portal-tabs" role="tablist" aria-label="Portal sections" data-testid="portal-tabs">
            {tabs.map((t) => {
              const Icon = TAB_ICONS[t.id] || Home;
              const selected = active === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  data-testid={`portal-tab-${t.id}`}
                  className={selected ? "portal-tab portal-tab-active" : "portal-tab"}
                  onClick={() => go(t.id)}
                >
                  <Icon size={15} /> {t.label}
                </button>
              );
            })}
          </div>

          {active === "home" && (
            <div data-testid="portal-panel-home" style={{ display: "grid", gap: 18 }}>
              <TiltCard intensity={2}>
                <div style={{ padding: 26 }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: "#1C1917" }}>Welcome</div>
                  <p style={{ fontSize: 13.5, color: "#57534E", marginTop: 8, lineHeight: 1.6 }}>
                    Use the tabs for Circulars, Gallery and Careers. Each tab works with its Drive folder. Change your password in its own tab when you are ready.
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 10, marginTop: 16 }}>
                    {tabs.filter((t) => t.id !== "home").map((t) => {
                      const Icon = TAB_ICONS[t.id] || FileText;
                      const level = t.id === "users" ? "admin" : t.id === "password" ? "your account" : (canEditTab(user, t.id) ? "edit" : "view");
                      return (
                        <button key={t.id} type="button" onClick={() => go(t.id)} data-testid={`portal-home-card-${t.id}`} className="btn-outline-3d" style={{ justifyContent: "flex-start", padding: "14px 16px", height: "auto", textAlign: "left" }}>
                          <Icon size={16} />
                          <span><b>{t.label}</b><div style={{ fontSize: 11, fontWeight: 700, color: "#EA580C", marginTop: 2 }}>{level}</div></span>
                        </button>
                      );
                    })}
                    {!tabs.some((t) => t.id !== "home" && t.id !== "password") && (
                      <div data-testid="portal-home-none" style={{ fontSize: 13, color: "#78716C" }}>No section access yet. Ask the administrator to grant Circulars, Gallery or Careers.</div>
                    )}
                  </div>
                </div>
              </TiltCard>
              {admin && (
                <TiltCard intensity={2}>
                  <div style={{ padding: 26 }} data-testid="inauguration-switch">
                    <div style={{ fontSize: 17, fontWeight: 800, color: "#1C1917" }}>Inauguration</div>
                    <p style={{ fontSize: 13.5, color: "#57534E", marginTop: 8, lineHeight: 1.6 }}>
                      On shows the public inauguration screen to each new browser session. Off sends visitors straight to the site.
                      If this switch cannot be loaded, the public site stays open.
                    </p>
                    <div data-testid="inauguration-status" style={{ marginTop: 12, fontSize: 13.5, fontWeight: 700, color: "#9A3412" }}>
                      {ceremonyBusy && "Saving…"}
                      {!ceremonyBusy && ceremonyState === "loading" && "Checking the inauguration switch…"}
                      {!ceremonyBusy && ceremonyState === "on" && "On"}
                      {!ceremonyBusy && ceremonyState === "off" && "Off"}
                      {!ceremonyBusy && ceremonyState === "unavailable" && "The inauguration switch is not available yet. Redeploy the API. Until then the public site stays open."}
                    </div>
                    <div role="group" aria-label="Inauguration" style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
                      <button type="button" className={ceremonyState === "on" ? "btn-red" : "btn-outline-3d"} data-testid="inauguration-on" aria-pressed={ceremonyState === "on"} onClick={() => setCeremony(true)} disabled={ceremonyBusy || ceremonyState === "on" || ceremonyState === "unavailable" || ceremonyState === "loading"} style={{ borderRadius: 999 }}>On</button>
                      <button type="button" className={ceremonyState === "off" ? "btn-red" : "btn-outline-3d"} data-testid="inauguration-off" aria-pressed={ceremonyState === "off"} onClick={() => setCeremony(false)} disabled={ceremonyBusy || ceremonyState === "off" || ceremonyState === "unavailable" || ceremonyState === "loading"} style={{ borderRadius: 999 }}>Off</button>
                    </div>
                    {ceremonyMsg && (
                      <div data-testid="inauguration-msg" style={{ marginTop: 12, fontSize: 13.5, fontWeight: 600, color: ceremonyMsg.ok ? "#15803D" : "#B91C1C", display: "flex", gap: 6 }}>
                        {ceremonyMsg.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />} {ceremonyMsg.text}
                      </div>
                    )}
                  </div>
                </TiltCard>
              )}
            </div>
          )}

          {active === "circulars" && canViewTab(user, "circulars") && (
            <div data-testid="portal-panel-circulars">
              <SiteDriveAdmin kind="circulars" allowEdit={canEditTab(user, "circulars")} />
            </div>
          )}
          {active === "gallery" && canViewTab(user, "gallery") && (
            <div data-testid="portal-panel-gallery">
              <SiteDriveAdmin kind="gallery" allowEdit={canEditTab(user, "gallery")} />
            </div>
          )}
          {active === "careers" && canViewTab(user, "careers") && (
            <div data-testid="portal-panel-careers">
              <CareersAdmin allowEdit={canEditTab(user, "careers")} />
            </div>
          )}
          {active === "password" && (
            <div data-testid="portal-panel-password">
              <TiltCard intensity={2}>
                <form onSubmit={changePwd} style={{ padding: 26, maxWidth: 480 }} data-testid="password-panel">
                  <div style={{ fontSize: 17, fontWeight: 800, color: "#1C1917", display: "flex", alignItems: "center", gap: 8 }}><KeyRound size={20} color="#F97316" /> Change password</div>
                  <p style={{ fontSize: 13, color: "#57534E", marginTop: 6 }}>Temporary passwords should be changed on first login. You can still use Circulars, Gallery and Careers while signed in.</p>
                  <input data-testid="current-password-input" type="password" required className="input-3d" placeholder="Current password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} style={{ marginTop: 14 }} />
                  <input data-testid="new-password-input" type="password" required minLength={8} className="input-3d" placeholder="New password (min 8 chars)" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} style={{ marginTop: 10 }} />
                  <button type="submit" className="btn-red" data-testid="change-password-btn" style={{ marginTop: 14, width: "100%", borderRadius: 999 }}>Update password</button>
                  {msg && <div data-testid="password-msg" style={{ marginTop: 12, fontSize: 13.5, fontWeight: 600, color: msg.ok ? "#15803D" : "#B91C1C", display: "flex", gap: 6 }}>{msg.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />} {msg.text}</div>}
                </form>
              </TiltCard>
            </div>
          )}
          {active === "users" && admin && (
            <div data-testid="portal-panel-users">
              <UsersAdmin />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
